import {AfterViewInit, Component, ContentChildren, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, inject} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, ReactiveFormsModule} from '@angular/forms';
import {Observable, of, Subject, Subscription} from 'rxjs';
import {SortableDirective, SortEvent} from '../../../_directives/sortable.directive';
import {debounceTime, filter, map} from 'rxjs/operators';
import {ActivatedRoute, ParamMap, Router, RouterLink} from '@angular/router';
import {GetId} from '../../../_utils/get-id';
import {FunctionBarComponent} from '../function-bar/function-bar.component';
import {TranslateModule} from '@ngx-translate/core';

// noinspection JSMismatchedCollectionQueryUpdate
@Component({
    selector: 'app-item-list',
    templateUrl: './item-list.component.html',
    styleUrls: ['./item-list.component.scss'],
    imports: [FunctionBarComponent, RouterLink, ReactiveFormsModule, TranslateModule]
})
export class ItemListComponent extends GetId implements OnInit, OnDestroy, AfterViewInit {

  @Input() filterFormPlaceholder: string;
  @Input() filterFunction: (filter: string, item) => boolean;
  @Input() searchMode: 'filter' | 'manual' = 'filter';

  @Input() addNewRouterLink: any[] | string;
  @Input() addNewRouterState?: {[k: string]: any};
  @Input() canAddNew$: Observable<boolean> = of(false);

  //When the parent component wants to change the list of items that are displayed, it'll send that list through this subject.
  @Input() items: Subject<any[]>; // The base collection we're going to filter and sort

  // The output list that the content child will base the table on
  @Output() filteredSortedItems: EventEmitter<any[]> = new EventEmitter<any[]>();
  @Output('manualSearch') _manualSearch: EventEmitter<string> = new EventEmitter<string>();

  inputId = this.getId();

  filterForm: UntypedFormGroup;
  itemsList: any[];

  // Instead of piping the canAddNew$ through an async, we'll subscribe to it in ngAfterViewInit() to avoid rechecking the observable when something marks the page dirty
  canAddNew = false;

  @ContentChildren(SortableDirective, {descendants: true})
  private headers: QueryList<SortableDirective>;

  private filteredItemsList: any[];

  //Holds the subscriptions for the sortable columns
  private sortableSubscriptions: Subscription[] = [];

  //Represents when the items are updated, we will automatically filter it based on the currently entered filter
  private itemsSubscription: Subscription;

  //When the filter is changed, update the filter for the currently known items.
  private filterSubscription: Subscription;

  private sortColumn: string = '';
  private sortDirection: 'asc' | 'desc' | '' = '';
  private urlSearch: string;

  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  constructor() {
    super();

    //Initialize the search if it's in the url
    this.urlSearch = this.route?.snapshot?.queryParamMap?.get('search');
    // console.debug(`Incoming search from url: ${this.urlSearch}`);

    //Monitor the queryParamMap in case the param on the url doesn't match what we think the search should be.
    //This is meant to catch back/forward buttons
    this.route.queryParamMap.pipe(
      map((m: ParamMap) => m.get('search')),
      filter(search => !!search && this.urlSearch !== search)
    ).subscribe(search => {
      console.debug(`Forward/back! Stored search = ${this.urlSearch}, url search = ${search}`);
      this.urlSearch = search;
      this.filterForm.controls.value.setValue(search);
      this.manualSearch();
    });

  }

  /** Creates a subscription that will fire whenever the items in this list are updated which will handle things like reapplying any filter */
  private onItemsUpdated() {
    if(!this.items) {
      console.warn('Items not set!');
    }
    this.itemsSubscription = this.items?.pipe(map(o => o ?? [])).subscribe((itemsList: any[]) => {
      this.itemsList = itemsList;
      this.filterItems();
    });
  }

  /** When the filter form field changes, apply the new filter to the item list */
  private onFilterChanges() {
    this.filterSubscription = this.filterForm.valueChanges.pipe(debounceTime(250)).subscribe(v => {
      console.debug('Filter changed!');
      this.filterItems(v.value);
    });
  }

  /** Filter the item list given a filter value */
  private filterItems(v: string = this.filterForm.get('value').value) {

    this.filteredItemsList = this.itemsList;
    const trimmedLowerCaseValue = v.trim().toLowerCase();

    if(!!this.filterFunction && trimmedLowerCaseValue !== '') {
      // console.debug(`Filtering ${this.itemsList.length} items by ${v}`);
      this.filteredItemsList = this.itemsList.filter(value => this.filterFunction(trimmedLowerCaseValue, value));
      // console.debug(`Filtered to ${this.filteredItemsList.length}`);
    } /*else {
      const msg = [!this.filterFunction ? 'no filter function' : null, trimmedLowerCaseValue === '' ? 'value blank' : null];
      console.info(`Not filtering anything because ${msg.filter(m => !!m).join(' and ')}!`);
    }*/

    this.sortItems();

  }

  /** Sort the item list according to things like a given column ascending/descending */
  private sortItems(column: string = this.sortColumn, direction: 'asc' | 'desc' | '' = this.sortDirection) {

    this.sortColumn = column;
    this.sortDirection = direction;

    if(column === '' || direction === '') {
      this.filteredSortedItems.emit(this.filteredItemsList);
    } else {
      this.filteredItemsList.sort((a, b) => {
        const res = a[column]?.localeCompare(b[column]) ?? 0;
        return direction === 'asc' ? res : -res;
      });
      this.filteredSortedItems.emit(this.filteredItemsList);
    }

  }

  /** init lifecycle */
  ngOnInit() {
    this.filterForm = this.formBuilder.group({
      value: this.urlSearch || ''
    });

    this.onItemsUpdated();
    if(this.searchMode === 'filter') {
      this.onFilterChanges();
    }

    if(this.urlSearch) {
      this.manualSearch();
    }

    this.canAddNew$?.subscribe(v => this.canAddNew = v);
  }

  /** destroy lifecycle */
  ngOnDestroy(): void {
    this.itemsSubscription?.unsubscribe();
    this.filterSubscription?.unsubscribe();
    this.sortableSubscriptions?.forEach(subscription => subscription.unsubscribe());
  }

  /** after the view has initialized lifecycle */
  ngAfterViewInit() {
    this.headers.forEach((sortableDirective: SortableDirective) => {
      this.sortableSubscriptions.push(
        sortableDirective.sort.subscribe(({column, direction}: SortEvent) => {
          this.headers.filter(h => h.appSortable !== column).forEach(h => h.direction = '');
          this.sortItems(column, direction);
        })
      );
    });
  }

  /** Clears the filter value and if it's a search form (instead of live filter) clear the item list as well */
  resetFilter() {
    this.filterForm.setValue({value: ''});
    if(this.searchMode === 'manual') {
      this.items.next([]);
    }
  }

  /** If the form field is a search (isntead of live filter), this method is used to emit the search term and update the URL to reflect that */
  manualSearch(v: string = this.filterForm.get('value').value) {
    console.debug(`Emitting manual search for ${v}`);
    this._manualSearch.emit(v);

    //Put the search term in the url so that forward/back and refresh can work a bit better
    this.router.navigate(['.'], {relativeTo: this.route, queryParams: {search: v}, state: {ignoreLoadingBar: true}});
  }

  /** Handles when the search/filter form is submitted, performing the search/filter appropriately */
  submit(formValue) {
    if(this.searchMode === 'manual') {
      this.manualSearch(formValue.value);
    } else {
      this.filterItems(formValue.value);
    }
  }

}
